<i18n src="./translations"></i18n>

<template>
  <div
    class="pdp-availability"
    :class="[classes, availabilityClass, { 'is-b2b': isBusinessCustomer }]"
    :data-preload="[$options.name]"
  >
    <Icon
      class="pdp-availability__shipping-icon"
      height="24"
      width="24"
      :name="
        ['instant', 'available_today'].includes(clearedState)
          ? 'shipping'
          : 'delivery_standard'
      "
    />

    <p class="pdp-availability__message">
      <span class="pdp-availability__label">
        {{ $t(`pdp-availability.${clearedState}.label`) }}
      </span>

      <span
        v-if="['available_today'].includes(clearedState) && !isBusinessCustomer"
        class="pdp-availability__text"
      >
        {{ $t(`pdp-availability.${clearedState}.text`) }}
      </span>
    </p>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import Icon from 'Components/00-generated/Icon';
import { mapState, mapGetters } from 'vuex';
export default {
  name: 'PdpAvailability',
  components: { Icon },
  mixins: [globalMixin],
  props: {
    state: {
      type: String,
      default: '',
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('core', {
      promiseBanner: ({ promiseBanner }) => promiseBanner || {},
    }),
    ...mapGetters('core', {
      isBusinessCustomer: 'isBusinessCustomer',
    }),
    clearedState() {
      if (
        this.promiseBanner.visible &&
        this.state.toLowerCase() === 'instant'
      ) {
        return 'available_today';
      }

      switch (this.state) {
        case 'instant':
        case 'INSTANT':
          return 'instant';
        case 'available':
        case 'AVAILABLE':
        case 'AVAILABLE_TODAY':
          return 'available_today';
        case '2to3workdays':
        case '2TO3WORKDAYS':
        case '2_TO_3_WORKDAYS':
          return '2_to_3_workdays';
        case '3to4workdays':
        case '3TO4WORKDAYS':
          return 'delivery_within_3_4_working_days';
        case '2to4weeks':
        case '2TO4WEEKS':
          return 'delivery_within_2_4_weeks';
        case 'DELAYED':
        case 'AVAILABLE_SOON':
        case 'AVAILABLE_ON_REQUEST':
          return 'delayed';
        case 'OUT_OF_STOCK':
          return 'out_of_stock';
        case 'notAvailable':
        case 'NOT_AVAILABLE':
        case 'NOTAVAILABLE':
        case 'UNAVAILABLE':
          return 'not_available';
        default:
          return 'not_available';
      }
    },
    availabilityClass() {
      switch (this.clearedState) {
        case 'instant':
          return this.promiseBanner.visible
            ? [
                'pdp-availability--instant',
                'qa-Availability--available',
                'qa-Availability--available-today',
              ]
            : ['pdp-availability--instant', 'qa-Availability--available'];
        case 'available_today':
          return this.promiseBanner.visible
            ? [
                'pdp-availability--available',
                'qa-Availability--available',
                'qa-Availability--available-today',
              ]
            : ['pdp-availability--available', 'qa-Availability--available'];
        case '2_to_3_workdays':
          return [
            'pdp-availability--2-3-workdays',
            'qa-Availability--available',
          ];
        case 'delivery_within_3_4_working_days':
          return [
            'pdp-availability--3-4-workdays',
            'qa-Availability--available_soon',
          ];
        case 'delivery_within_2_4_weeks':
          return [
            'pdp-availability--2-4-weeks',
            'qa-Availability--available_soon',
          ];
        case 'delayed':
          return [
            'pdp-availability--delayed',
            'qa-Availability--available_soon',
          ];
        case 'out_of_stock':
          return [
            'pdp-availability--out-of-stock',
            'qa-Availability--available_soon',
          ];
        case 'not_available':
          return [
            'pdp-availability--not-available',
            'qa-Availability--not_available',
          ];
        default:
          return [
            'pdp-availability--not-available',
            'qa-Availability--not_available',
          ];
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import 'mixins';

.pdp-availability {
  display: flex;
  align-items: center;
  margin-top: var(--space-1);

  --indicative-color: var(--color-nero);

  &--instant,
  &--available,
  &--2-3-workdays,
  &--3-4-workdays {
    --indicative-color: var(--color-japanese-laurel);
  }

  &.is-b2b {
    &.pdp-availability--2-3-workdays,
    &.pdp-availability--3-4-workdays {
      --indicative-color: var(--color-24-blue);
    }
  }

  &--2-4-weeks,
  &--delayed {
    --indicative-color: var(--color-amber);
  }

  &--not-available,
  &--out-of-stock {
    --indicative-color: var(--color-monza);
  }
}

.pdp-availability__shipping-icon {
  margin-right: 10px;
  fill: var(--indicative-color);

  @include icon-dimensions(24px);
}

.pdp-availability__message {
  @include typography-size(var(--font-size-M), 19px);
}

.pdp-availability__label {
  color: var(--color-nero);
  font-size: var(--font-size-SM);
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: normal;
  text-align: left;
}

.pdp-availability__text {
  font-size: var(--font-size-SM);
  color: var(--color-nero);
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: normal;
  text-align: left;
  display: block;
  margin-top: 3px;
}

.pdp-availability--instant .pdp-availability__label,
.pdp-availability--available .pdp-availability__label {
  background: var(--color-japanese-laurel);
  color: var(--color-white);
  padding: 3px var(--space-1);
  border-radius: 4px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: normal;
  text-align: left;
}

.pdp-availability--available {
  align-items: normal;
}
</style>
